import React, { FC, ReactNode } from 'react'

import {
  IconCheck,
  IconClose,
  IconInfo,
  IconReload,
  IconTrash,
} from '@avantstay/backoffice-vectors'

import { Tooltip } from '../../../Tooltip'
import {
  FilePreviewActionProps,
  FilePreviewType,
  FileResolutionProps,
  FileViewMode,
} from '../../UploadArea.types'
import { DownloadPhoto } from '../DownloadPhoto'
import { FilePreviewStyles as Preview } from '../FilePreview'
import * as S from './FilePreviewActions.styles'

interface ActionsProps extends FilePreviewActionProps {
  decodedFileName: string
  isImageCover: boolean
  index: number
  assetId: string
  isLowResolution: boolean
  isFileImage: boolean
  isFileUploaded: boolean
  filePosition: number
  resolution?: FileResolutionProps
  isSelecting?: boolean
  isFileSelected?: boolean
  isPhotoEnable?: boolean
  filePreviewType?: FilePreviewType
  fileUrl?: string
  hasError?: boolean
  fileCustomInfo?: ReactNode
  onRetryUpload: VoidFunction
  onRemoveFile?: VoidFunction
  onSelectFile: VoidFunction
  onToggleEnableAsset?: VoidFunction
}

export const FilePreviewActions: FC<ActionsProps> = ({
  decodedFileName,
  assetId,
  isImageCover,
  isLowResolution,
  isFileImage,
  isFileUploaded,
  filePosition,
  filePreviewActions,
  resolution,
  isSelecting,
  isFileSelected,
  isPhotoEnable,
  hasError,
  onRetryUpload,
  fileViewMode,
  filePreviewType,
  fileCustomInfo,
  onRemoveFile,
  index,
  onSelectFile,
  fileUrl,
  onToggleEnableAsset,
}) => {
  const isPhotoGallery = filePreviewType === FilePreviewType.photoGallery
  const isReferencePhoto = filePreviewType === FilePreviewType.referencePhoto

  return (
    <>
      {isPhotoGallery ? (
        <S.DetailsContainer
          $isCover={isImageCover}
          warning={isLowResolution}
          error={hasError}
          $viewMode={fileViewMode}
        >
          <S.FileTitle>
            <S.FileIndex error={hasError}>{filePosition}</S.FileIndex>
            {hasError ? <Preview.Error> &bull; Upload failed</Preview.Error> : null}
          </S.FileTitle>

          <S.DetailActions>
            {!hasError && !isSelecting ? (
              <Tooltip
                content={
                  <S.FileInfoContainer>
                    <S.FileInfoLabel>
                      Name: <S.FileInfoContent>{decodedFileName}</S.FileInfoContent>
                    </S.FileInfoLabel>
                    {isFileImage && resolution?.height && resolution?.width ? (
                      <S.FileInfoLabel>
                        Dimension:
                        <S.FileInfoContent>
                          {resolution.height} x {resolution.width}
                        </S.FileInfoContent>
                      </S.FileInfoLabel>
                    ) : null}
                    {fileCustomInfo}
                  </S.FileInfoContainer>
                }
                placement="top"
              >
                <IconInfo />
              </Tooltip>
            ) : null}

            {!isSelecting && filePreviewActions
              ? isFileUploaded &&
                fileViewMode === FileViewMode.grid &&
                filePreviewActions({
                  index,
                  assetId,
                  warning: isLowResolution,
                  error: hasError,
                })
              : null}

            {hasError ? (
              <Tooltip content="Retry" placement="top">
                <S.ActionIconWrapper>
                  <IconReload onClick={onRetryUpload} />
                </S.ActionIconWrapper>
              </Tooltip>
            ) : null}

            {isFileUploaded && !isSelecting ? (
              <DownloadPhoto decodedFileName={decodedFileName} fileUrl={fileUrl} />
            ) : null}

            {!isSelecting && onRemoveFile ? (
              <Tooltip content="Remove" placement="top">
                <S.ActionIconWrapper>
                  <IconTrash width={20} height={20} onClick={onRemoveFile} />
                </S.ActionIconWrapper>
              </Tooltip>
            ) : null}

            {isFileUploaded && onToggleEnableAsset ? (
              <S.SelectImageButton isSelected={isPhotoEnable} onClick={onToggleEnableAsset}>
                <IconCheck />
              </S.SelectImageButton>
            ) : null}

            {isFileUploaded && isSelecting ? (
              <S.SelectImageButton isSelected={isFileSelected} onClick={onSelectFile}>
                <IconCheck />
              </S.SelectImageButton>
            ) : null}
          </S.DetailActions>
        </S.DetailsContainer>
      ) : (
        <S.ActionWrapper $primaryTheme={isReferencePhoto}>
          {!isSelecting && filePreviewActions
            ? isFileUploaded &&
              fileViewMode === FileViewMode.grid &&
              filePreviewActions({
                index,
                assetId,
                warning: isLowResolution,
                error: hasError,
              })
            : null}

          {!hasError ? (
            <Tooltip content="Remove" placement="top">
              <S.ActionIconWrapper>
                {isReferencePhoto ? (
                  <IconTrash width={20} height={20} onClick={onRemoveFile} />
                ) : (
                  <IconClose onClick={onRemoveFile} />
                )}
              </S.ActionIconWrapper>
            </Tooltip>
          ) : null}
        </S.ActionWrapper>
      )}

      {filePreviewActions
        ? fileViewMode !== FileViewMode.grid &&
          filePreviewActions({
            index,
            assetId,
            warning: isLowResolution,
            error: hasError,
          })
        : null}
    </>
  )
}
